/**
 * Spanish number-to-words for printed receipt totals: "Son: Cincuenta 00/100
 * Bolivianos" / "Son: Mil Doscientos 50/100 Bolivianos".
 *
 * Handles the irregular cases by hand rather than a general algorithm, since
 * those are exactly where a naive concatenation breaks:
 *   - "veintiuno".."veintinueve" are one word, not "veinte y uno"
 *   - "cien" only alone; "ciento" when followed by more (ciento uno)
 *   - "quinientos", "setecientos", "novecientos" are irregular, not
 *     <digit>+"cientos"
 *   - "un mil" is wrong Spanish — it's just "mil"
 *   - "millón" singular takes no "de" before another number in Spanish
 *     accounting style ("un millón cincuenta"), but "millones" (plural) does
 *     when followed by nothing — kept simple since receipts won't reach this
 *     range in practice; three-digit millions are unsupported (see MAX_SAFE).
 *
 * Cents are printed as a literal "NN/100" per the paper convention, not
 * spelled out.
 */

const UNITS = [
  '', 'uno', 'dos', 'tres', 'cuatro', 'cinco', 'seis', 'siete', 'ocho', 'nueve',
];

const TEENS = [
  'diez', 'once', 'doce', 'trece', 'catorce', 'quince',
  'dieciséis', 'diecisiete', 'dieciocho', 'diecinueve',
];

const TWENTIES = [
  'veinte', 'veintiuno', 'veintidós', 'veintitrés', 'veinticuatro', 'veinticinco',
  'veintiséis', 'veintisiete', 'veintiocho', 'veintinueve',
];

const TENS = [
  '', '', '', 'treinta', 'cuarenta', 'cincuenta',
  'sesenta', 'setenta', 'ochenta', 'noventa',
];

const HUNDREDS = [
  '', 'ciento', 'doscientos', 'trescientos', 'cuatrocientos', 'quinientos',
  'seiscientos', 'setecientos', 'ochocientos', 'novecientos',
];

/** Converts 0–999 to words. */
function threeDigitsToWords(n: number): string {
  if (n === 0) return '';
  if (n === 100) return 'cien';

  const h = Math.floor(n / 100);
  const rest = n % 100;
  const parts: string[] = [];

  if (h > 0) parts.push(HUNDREDS[h]);

  if (rest >= 20) {
    const tens = Math.floor(rest / 10);
    const units = rest % 10;
    if (rest < 30) {
      parts.push(TWENTIES[units]);
    } else if (units === 0) {
      parts.push(TENS[tens]);
    } else {
      parts.push(`${TENS[tens]} y ${UNITS[units]}`);
    }
  } else if (rest >= 10) {
    parts.push(TEENS[rest - 10]);
  } else if (rest > 0) {
    parts.push(UNITS[rest]);
  }

  return parts.join(' ');
}

/** Converts a non-negative integer (supports up to 999,999,999) to Spanish words. */
export function integerToWords(n: number): string {
  if (!Number.isFinite(n) || n < 0) return '';
  if (n === 0) return 'cero';

  const millions  = Math.floor(n / 1_000_000);
  const thousands = Math.floor((n % 1_000_000) / 1000);
  const rest      = n % 1000;

  const parts: string[] = [];

  if (millions > 0) {
    parts.push(millions === 1 ? 'un millón' : `${apocopeUno(threeDigitsToWords(millions))} millones`);
  }

  if (thousands > 0) {
    parts.push(thousands === 1 ? 'mil' : `${apocopeUno(threeDigitsToWords(thousands))} mil`);
  }

  if (rest > 0) {
    parts.push(threeDigitsToWords(rest));
  }

  return parts.join(' ');
}

function capitalize(s: string): string {
  return s.length ? s[0].toUpperCase() + s.slice(1) : s;
}

/**
 * "uno" apocopates to "un" immediately before a noun ("mil", "millones"),
 * regardless of that noun's grammatical number: 21 mil is "veintiún mil",
 * never "veintiuno mil". "veintiuno" additionally gains an accent: "veintiún".
 */
function apocopeUno(words: string): string {
  if (words.endsWith('veintiuno')) return words.slice(0, -3) + 'ún';
  if (words.endsWith('uno')) return words.slice(0, -1);
  return words;
}

/**
 * "Son: <Amount in words> NN/100 <Currency>" — the exact convention on every
 * paper receipt sampled. `amount` is the total in major units (e.g. 4200.50).
 */
export function amountToWords(amount: number, currency: 'BOB' | 'USD' = 'BOB'): string {
  const rounded = Math.round(Math.abs(amount) * 100) / 100;
  const whole = Math.floor(rounded);
  const cents = Math.round((rounded - whole) * 100);

  const currencyWord = currency === 'USD' ? 'Dólares' : 'Bolivianos';
  const words = capitalize(integerToWords(whole));

  return `${words} ${String(cents).padStart(2, '0')}/100 ${currencyWord}`;
}
